import React from 'react';
import { FieldError } from 'react-hook-form';
import type { GestureResponderEvent } from 'react-native';

import { BoxProps, Paragraph, Text } from '@rover/kibble/core';
import type { ThemeTextSizes } from '@rover/kibble/styles';
import { A11yHidden } from '@rover/kibble/styles';

import translateFieldError from '../core/translateFieldError';

export type SharedTypographyProps = {
  primaryLabel: string;
  primaryLabelSize?: '100' | '200' | '300';
  helperMessage?: string | React.ComponentType<{ helperMessageId: string }>;
  secondaryLabel?: string;
  disabled?: boolean;
  required?: boolean;
  primaryLabelA11yHidden?: boolean;
  /** hideRequiredAsterisk should be only set to true for a single-input form */
  hideRequiredAsterisk?: boolean;
};

export type PrimaryLabelVariants =
  | 'default'
  | 'option-label'
  | 'legalConsent'
  | 'order-option'
  | 'compact';

export interface PrimaryLabelProps {
  primaryLabel: string;
  primaryLabelSize?: '100' | '200' | '300';
  required: boolean;
  id?: string;
  webInputId?: string;
  primaryLabelA11yHidden?: boolean;
  accessibilityLabel?: string;
  sx?: BoxProps['sx'];
  as?: 'label' | 'legend';
  // MouseEvent on web, GestureResponderEvent on native
  // to do this right we need to split files, I'll do it later
  onClick?: React.MouseEventHandler<HTMLElement> | GestureResponderEvent;
  variant?: PrimaryLabelVariants;
  primaryLabelVariant?: PrimaryLabelVariants;
  hideRequiredAsterisk?: boolean;
}

type PrimaryLabelInternalTextProps = {
  id?: string;
  testID?: string;
  as?: 'label' | 'legend';
  htmlFor?: string;
  'data-testid'?: string;
  fontWeight?: 'semibold' | 'regular';
  textColor?: 'primary' | 'secondary' | 'tertiary';
  size?: ThemeTextSizes;
  height?: number;
};

export const PrimaryLabel = ({
  primaryLabel,
  accessibilityLabel,
  required,
  id,
  webInputId,
  primaryLabelA11yHidden,
  sx,
  as = 'label',
  onClick,
  variant = 'default',
  primaryLabelSize,
  hideRequiredAsterisk = false, // should be only set true for a single-input form
}: PrimaryLabelProps): JSX.Element => {
  const textProps: PrimaryLabelInternalTextProps = process.env.JS_ENV_NATIVE
    ? {
        testID: 'kff-primary-label',
      }
    : {
        as,
        id,
        htmlFor: webInputId,
        'data-testid': 'kff-primary-label',
      };

  let Component: typeof Text | typeof Paragraph = Text;
  if (variant === 'option-label' || variant === 'legalConsent') {
    Component = Paragraph;
    textProps.fontWeight = 'regular';
  }

  if (variant === 'default') {
    textProps.fontWeight = 'semibold';
  }

  if (variant === 'legalConsent') {
    textProps.textColor = 'tertiary';
    textProps.size = '100';
  }

  if (variant === 'order-option') {
    textProps.fontWeight = 'semibold';
    textProps.size = '100';
  }

  if (variant === 'compact') {
    textProps.fontWeight = 'semibold';
    textProps.size = '100';
    textProps.textColor = 'secondary';
  }

  // primaryLabelSize overrides variant styles
  if (primaryLabelSize) {
    textProps.size = primaryLabelSize;
  }

  let finalSx = sx;

  if (primaryLabelA11yHidden && process.env.JS_ENV_NATIVE) {
    // There isn't clear documentation on how to a11y hide a label in native
    // removing the label entirely causes issues with RadioField and CheckboxField,
    // instead we'll try setting the height&mb to 0 so the element is present but invisible
    textProps.height = 0;
    finalSx = { ...sx, mb: 0 };
  } else if (primaryLabelA11yHidden) {
    finalSx = { A11yHidden, ...sx };
  }

  return (
    <Component
      {...textProps}
      accessibilityLabel={accessibilityLabel}
      sx={finalSx}
      mb="1x"
      // This actually works just fine, but getting TS to be happy is a pain
      // I'll make this better later
      // @ts-expect-error
      onClick={onClick}
    >
      {primaryLabel}
      {required && !hideRequiredAsterisk && <Component aria-hidden> *</Component>}
    </Component>
  );
};
export interface SecondaryLabelProps {
  secondaryLabel?: string;
  labelId?: string;
  mb?: BoxProps['mb'];
}

export const SecondaryLabel = ({
  secondaryLabel,
  labelId,
  mb = '1x',
}: SecondaryLabelProps): JSX.Element | null => {
  if (!secondaryLabel) return null;

  const props = process.env.JS_ENV_NATIVE
    ? {
        testID: 'kff-secondary-label',
      }
    : ({
        id: labelId,
        'data-testid': 'kff-secondary-label',
        as: 'span',
      } as const);

  return (
    <Paragraph textColor="tertiary" mb={mb} size="100" {...props}>
      {secondaryLabel}
    </Paragraph>
  );
};

export interface FeedbackMessageProps {
  fieldError?: FieldError;
  fieldErrorId?: string;
  helperMessage?: string | React.ComponentType<{ helperMessageId: string }>;
  helperMessageId?: string;
  translatedFieldErrorMessage?: string;
}

export const FeedbackMessageCommonStyles = {
  as: 'span',
  mb: '0x',
  mt: '1x',
  size: '100',
} as const;

interface CustomHelperMessageWrapperProps {
  helperMessageId?: string;
  children: React.ReactNode;
}

export const CustomHelperMessageWrapper: React.FC<CustomHelperMessageWrapperProps> = ({
  helperMessageId,
  children,
}) => {
  const platformSpecificProps = process.env.JS_ENV_NATIVE
    ? {}
    : {
        id: helperMessageId,
        'data-testid': 'kff-help-msg',
      };

  return (
    <Paragraph {...FeedbackMessageCommonStyles} textColor="tertiary" {...platformSpecificProps}>
      {children}
    </Paragraph>
  );
};

export const FeedbackMessage = ({
  fieldError,
  fieldErrorId,
  helperMessage,
  helperMessageId,
  translatedFieldErrorMessage,
}: FeedbackMessageProps): JSX.Element | null => {
  let errorMessageComponent: JSX.Element | null = null;
  let helperMessageComponent: JSX.Element | null = null;

  let errorMessageText: string | undefined;
  if (translatedFieldErrorMessage) {
    errorMessageText = translatedFieldErrorMessage;
  } else if (fieldError) {
    errorMessageText = translateFieldError(fieldError);
  }

  if (errorMessageText) {
    const platformSpecificProps = process.env.JS_ENV_NATIVE
      ? {}
      : {
          id: fieldErrorId,
        };
    errorMessageComponent = (
      <Paragraph
        {...FeedbackMessageCommonStyles}
        {...platformSpecificProps}
        textColor="error"
        role="alert"
      >
        {errorMessageText}
      </Paragraph>
    );
  }

  if (helperMessage) {
    if (typeof helperMessage === 'function') {
      helperMessageComponent = React.createElement(
        helperMessage,
        helperMessageId ? { helperMessageId } : undefined
      );
    } else {
      const platformSpecificProps = process.env.JS_ENV_NATIVE
        ? {}
        : { id: helperMessageId, 'data-testid': 'kff-help-msg' };
      helperMessageComponent = (
        <Paragraph {...FeedbackMessageCommonStyles} {...platformSpecificProps} textColor="tertiary">
          {helperMessage}
        </Paragraph>
      );
    }
  }

  return (
    <>
      {helperMessageComponent}
      {errorMessageComponent}
    </>
  );
};
